import { useEffect, useState } from "react";
import { AppShell } from "./components/AppShell";
import { AIAnalystPage } from "./features/analyst/AIAnalystPage";
import { AssistantsPage } from "./features/assistants/AssistantsPage";
import { DashboardCanvasPage } from "./features/canvas/DashboardCanvasPage";
import { DocumentCanvasPage } from "./features/document-canvas/DocumentCanvasPage";
import { LoginPage } from "./features/auth/LoginPage";
import { GroupsPage } from "./features/access/GroupsPage";
import { DataSourcesPage } from "./features/data-sources/DataSourcesPage";
import { MetricsCatalogPage } from "./features/metrics/MetricsCatalogPage";
import { StaticWireframePage } from "./features/static/StaticWireframePage";
import { WhatIfPage } from "./features/what-if/WhatIfPage";
import {
  WorkspacePage,
  type WorkspaceView,
} from "./features/workspace/WorkspacePage";
import { authApi, sourcesApi } from "./services/api";
import {
  analystName,
  isDataDomain,
  type DataDomain,
} from "./services/dataDomain";
import {
  storage,
  type SelectedContext,
} from "./services/storage";
import type { Metric, PageId, UserSession } from "./types";

const functionalPages = new Set<PageId>([
  "assistants",
  "analyst",
  "workspace",
  "my-dashboards",
  "my-reports",
  "my-artifacts",
  "dashboard-canvas",
  "report-canvas",
  "artifact-canvas",
  "data-sources",
  "metrics",
  "what-if",
  "groups",
]);

// My Workspace's Dashboards, Reports and Artifacts entries are views of the
// one workspace screen.
const workspaceViews = {
  workspace: "dashboards",
  "my-dashboards": "dashboards",
  "my-reports": "reports",
  "my-artifacts": "artifacts",
} as const satisfies Partial<Record<PageId, WorkspaceView>>;

function isWorkspacePage(page: PageId): page is keyof typeof workspaceViews {
  return page in workspaceViews;
}

/** The blank canvas each workspace view's Create button opens. */
const canvasPages: Record<WorkspaceView, PageId> = {
  dashboards: "dashboard-canvas",
  reports: "report-canvas",
  artifacts: "artifact-canvas",
};

export default function App() {
  // The stored session is only a hint. The API decides whether it is still
  // valid, so the UI stays in a resolving state until it answers.
  const [session, setSession] = useState<UserSession | null>(() => storage.session());
  const [resolvingSession, setResolvingSession] = useState(true);
  // The landing page asks which assistant and which model, so a session never
  // starts against whichever context happened to be pinned last. A returning
  // user who has already answered both goes straight to the conversation.
  const [selectedAssistant, setSelectedAssistant] = useState<string | null>(
    () => storage.selectedAssistant(),
  );
  const [page, setPage] = useState<PageId>(() =>
    storage.selectedAssistant() && storage.selectedContext()
      ? "analyst"
      : "assistants",
  );
  const [metrics, setMetrics] = useState<Metric[]>(() => storage.metrics());
  // Shared rather than read independently by the Data Sources and Analyst
  // pages. The analyst stays mounted while another page is open, so writing
  // only localStorage left its in-memory selection stale until a full reload.
  const [selectedContext, setSelectedContext] =
    useState<SelectedContext | null>(() => storage.selectedContext());
  // What the context layer says each source is classified as now. A stored
  // selection only knows the domain at the moment it was made, and a source
  // pinned from inside a conversation carries none at all.
  const [domains, setDomains] = useState<Record<string, DataDomain | null>>({});
  const signedIn = session !== null;
  const selectedId = selectedContext?.contextId ?? "";

  useEffect(() => {
    if (!signedIn) return;
    let cancelled = false;
    sourcesApi
      .list()
      .then((sources) => {
        if (cancelled) return;
        setDomains(
          Object.fromEntries(
            sources.map((source) => [
              source.id,
              isDataDomain(source.dataDomain) ? source.dataDomain : null,
            ]),
          ),
        );
      })
      .catch(() => {
        // The name is cosmetic. Without the context layer the stored
        // selection's own domain, or the generic name, is shown instead.
      });
    return () => {
      cancelled = true;
    };
  }, [signedIn, selectedId]);

  const selectedDomain = selectedContext
    ? selectedContext.contextId in domains
      ? domains[selectedContext.contextId]
      : selectedContext.dataDomain
    : null;
  const analyst = analystName(selectedDomain);

  useEffect(() => {
    let cancelled = false;
    authApi
      .session()
      .then((current) => {
        if (cancelled) return;
        if (current) {
          storage.saveSession(current);
          setSession(current);
        } else {
          storage.clearSession();
          setSession(null);
        }
      })
      .catch(() => {
        // The service is unreachable. Treat the cached session as untrusted
        // rather than admitting the user on stale local state.
        if (cancelled) return;
        storage.clearSession();
        setSession(null);
      })
      .finally(() => {
        if (!cancelled) setResolvingSession(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function authenticated(next: UserSession) {
    storage.saveSession(next);
    setSession(next);
    window.scrollTo({ top: 0 });
  }

  async function logout() {
    await authApi.logout();
    storage.clearSession();
    setSession(null);
    // Back to the landing choice rather than the conversation: the next person
    // to sign in on this machine should be asked, not handed the last
    // session's assistant.
    setPage("assistants");
    window.scrollTo({ top: 0 });
  }

  function updateMetrics(next: Metric[]) {
    storage.saveMetrics(next);
    setMetrics(next);
  }

  function selectContext(next: SelectedContext) {
    storage.saveSelectedContext(next);
    setSelectedContext(next);
  }

  /** Both halves of the landing choice, then straight into the conversation. */
  function chooseAssistant(assistant: string, context: SelectedContext) {
    storage.saveSelectedAssistant(assistant);
    setSelectedAssistant(assistant);
    selectContext(context);
    setPage("analyst");
    window.scrollTo({ top: 0 });
  }

  if (resolvingSession) {
    return (
      <main className="session-resolving" role="status" aria-live="polite">
        Checking your session…
      </main>
    );
  }

  if (!session) return <LoginPage onAuthenticated={authenticated} />;

  // The analyst is absent here on purpose: see the render below.
  let content = null;
  if (page === "assistants") {
    content = (
      <AssistantsPage
        selectedContext={selectedContext}
        selectedAssistant={selectedAssistant}
        onChoose={chooseAssistant}
      />
    );
  } else if (page === "data-sources") {
    content = (
      <DataSourcesPage
        selectedContext={selectedContext}
        onSelectContext={selectContext}
      />
    );
  } else if (page === "metrics") {
    content = <MetricsCatalogPage metrics={metrics} onChange={updateMetrics} />;
  } else if (isWorkspacePage(page)) {
    // Dashboards are read from the API rather than passed down: auto-save is
    // meaningless against browser storage, which is per-machine and clearable.
    content = (
      <WorkspacePage
        key={page}
        session={session}
        view={workspaceViews[page]}
        onCreate={() => {
          // The empty state still offers to ask the analyst instead.
          setPage("analyst");
          window.scrollTo({ top: 0 });
        }}
        onStartCanvas={(view) => {
          setPage(canvasPages[view]);
          window.scrollTo({ top: 0 });
        }}
      />
    );
  } else if (page === "dashboard-canvas") {
    content = (
      <DashboardCanvasPage
        selectedContext={selectedContext}
        onSelectContext={selectContext}
        onBack={() => {
          setPage("my-dashboards");
          window.scrollTo({ top: 0 });
        }}
      />
    );
  } else if (page === "report-canvas" || page === "artifact-canvas") {
    const variant = page === "report-canvas" ? "report" : "artifact";
    content = (
      <DocumentCanvasPage
        key={page}
        variant={variant}
        selectedContext={selectedContext}
        onSelectContext={selectContext}
        onBack={() => {
          setPage(variant === "report" ? "my-reports" : "my-artifacts");
          window.scrollTo({ top: 0 });
        }}
      />
    );
  } else if (page === "groups") {
    content = <GroupsPage />;
  } else if (page === "what-if") {
    content = (
      <WhatIfPage
        selectedContext={selectedContext}
        onSelectContext={selectContext}
      />
    );
  } else if (page !== "analyst") {
    content = <StaticWireframePage page={page} />;
  }

  return (
    <AppShell
      page={page}
      session={session}
      analystName={analyst}
      onNavigate={setPage}
      onLogout={logout}
    >
      {/* Hidden rather than unmounted, so a conversation survives a trip to
          another page: its transcript, the dashboards composed in it, the
          reasoning trace, a draft in the composer, and a turn still streaming.
          Swapped out, all of that was local state that went with it, leaving
          history as the only way back to work the user could still see a moment
          earlier. "New chat" remains the way to clear it. */}
      <div data-functional hidden={page !== "analyst"}>
        <AIAnalystPage
          onNavigate={setPage}
          active={page === "analyst"}
          selectedContext={selectedContext}
          onSelectContext={selectContext}
          analystName={isDataDomain(selectedDomain) ? analyst : undefined}
        />
      </div>
      {content && (
        <div data-functional={functionalPages.has(page)}>{content}</div>
      )}
    </AppShell>
  );
}
